// The warm-up's step runner: the steps one after another, in order, a pause between them so the
// screen paints the next label and the line. A step that throws counts as done (logged as before
// with console.warn): the screen can never hang on a failed step. No time limit otherwise (Axel
// 2026-10-10). Pure: the screen (shell/UpdateGate.tsx) passes the steps and the pause.
import type { WarmupContext, WarmupLabel, WarmupStep } from './step';

export interface WarmupProgress {
  /** The running step (the last one once done). */
  label: WarmupLabel | null;
  /** Steps done + the running step's fraction, over all steps: 0..1. */
  fraction: number;
  done: boolean;
}

export interface RunDeps {
  pause: () => Promise<void>;
  appReady: Promise<void>;
  onProgress: (p: WarmupProgress) => void;
  warn?: (msg: string, err: unknown) => void;
  /** Wraps each step (the screen times them as `vu:warmup-<id>`). */
  measure?: <T>(id: string, f: () => Promise<T>) => Promise<T>;
}

export async function runWarmup(steps: readonly WarmupStep[], deps: RunDeps): Promise<void> {
  const total = steps.length;
  const warn = deps.warn ?? ((m, e) => console.warn(m, e));
  const measure = deps.measure ?? ((_id, f) => f());
  let last: WarmupLabel | null = null;
  for (const [i, step] of steps.entries()) {
    last = step.label;
    let part = 0;
    const report = () => deps.onProgress({ label: step.label, fraction: (i + part) / total, done: false });
    report();
    await deps.pause();
    const ctx: WarmupContext = {
      progress: (f) => {
        const next = Math.min(1, Math.max(part, f));
        if (next === part) return;
        part = next;
        report();
      },
      pause: deps.pause,
      appReady: deps.appReady,
    };
    try {
      await measure(step.id, () => step.run(ctx));
    } catch (err) {
      warn(`[warmup] ${step.id} failed`, err);
    }
  }
  deps.onProgress({ label: last, fraction: 1, done: true });
}
